// Scribe <-> MindVault connection rule (client side of the pricing/access model).
//
// The rule itself lives in one place — the get_scribe_connection_statuses RPC:
//   connected = (paid booking OR active access code OR active basic/pro
//                subscription) AND MV user consent
// Components must resolve MindVault accounts through these helpers instead of
// scanning mindvault_accounts + decrypt_text themselves: the RPC only reveals
// data for connected clients, runs server-side, and gates by the calling
// therapist's auth uid.

import { supabase } from '../supabaseClient.js';

// What the therapist has paid for, ignoring consent. Mirrors the RPC's
// `entitlement` column; used to keep a paid-but-unshared client from being
// labelled "Not connected", which reads as "you have no relationship with
// this client" to a therapist who is being billed monthly for exactly that
// relationship. Key off this for anything paid-vs-unpaid — the reason
// vocabulary (no_account | no_consent | no_payment_source | booking_lapsed)
// belongs to the RAG lineage, and its no_consent doesn't distinguish paid
// from unpaid. The one sanctioned reason consumer is REASON.BOOKING_LAPSED
// (D-021): it exists precisely to tell "this booking went stale" apart from
// "no relationship at all".
export const ENTITLEMENT = {
  BOOKING: 'booking',
  ACCESS_CODE: 'access_code',
  ACCESS_CODE_PENDING: 'access_code_pending', // code bought, checkout not settled
  SUBSCRIPTION: 'subscription', // active basic/pro plan covers every client
};

// The therapist has paid for this client, whatever the client has shared.
// Purchase pending counts: they've committed, so never re-pitch the code.
export const isEntitled = (entitlement) =>
  entitlement === ENTITLEMENT.BOOKING ||
  entitlement === ENTITLEMENT.ACCESS_CODE ||
  entitlement === ENTITLEMENT.ACCESS_CODE_PENDING ||
  entitlement === ENTITLEMENT.SUBSCRIPTION;

// D-021: the client booked this therapist on the marketplace, but the most
// recent booking is older than 30 days, so the free connection lapsed. The
// UI shows a reactivation hint instead of the stranger copy.
export const REASON = {
  BOOKING_LAPSED: 'booking_lapsed',
};

// Connected but data-blind: the link is live/paid, the client just hasn't
// turned on sharing. Shared wording so the list and the dashboard agree.
export const RESTRICTED_ACCESS_LABEL = 'Connected · restricted access';
export const RESTRICTED_ACCESS_TOOLTIP =
  'You are connected to this client, but they have not shared their MindVault ' +
  'data with you yet. Your notes still guide their MindVault chatbot.';

// statuses: 'booking' | 'access_code' | null (not connected / no consent)
export async function getConnectionStatuses(therapistUserId, emails, { throwOnError = false } = {}) {
  const cleaned = [...new Set(
    (emails || []).map((e) => (e ?? '').trim().toLowerCase()).filter(Boolean)
  )];
  if (!therapistUserId || !cleaned.length) return new Map();

  const { data, error } = await supabase.rpc('get_scribe_connection_statuses', {
    p_therapist_user_id: therapistUserId,
    p_client_emails: cleaned,
  });
  if (error) {
    console.error('Error fetching scribe connection statuses:', error);
    if (throwOnError) throw error;
    return new Map();
  }

  const byEmail = new Map();
  (data || []).forEach((row) => {
    byEmail.set(row.client_email, {
      connectionStatus: row.connection_status || null,
      mindvaultAccountId: row.mindvault_account_id || null,
      connected: !!row.connection_status,
      // entitlement/has_consent arrive with the 20260811 migration; before
      // it lands they are undefined and callers fall back to the older,
      // coarser "connected or not" rendering.
      entitlement: row.entitlement || null,
      hasConsent: !!row.has_consent,
      reason: row.reason || null,
    });
  });
  return byEmail;
}

// Single-patient convenience. Returns
//   { connected, connectionStatus, mindvaultAccountId, hasMvAccount,
//     entitlement, hasConsent, awaitingConsent, bookingLapsed }
export async function getConnectionStatus(therapistUserId, email, options) {
  const key = (email ?? '').trim().toLowerCase();
  const byEmail = await getConnectionStatuses(therapistUserId, [key], options);
  const s = byEmail.get(key);
  return {
    connected: !!s?.connected,
    connectionStatus: s?.connectionStatus || null,
    mindvaultAccountId: s?.mindvaultAccountId || null,
    hasMvAccount: !!s?.mindvaultAccountId,
    entitlement: s?.entitlement || null,
    hasConsent: !!s?.hasConsent,
    awaitingConsent: !s?.connected && isEntitled(s?.entitlement),
    bookingLapsed: s?.reason === REASON.BOOKING_LAPSED,
  };
}

// Consent-gated profile prefill for the New-Client form. The RPC
// (get_mindvault_profile_by_email, migration 20260928000001) returns a row
// ONLY when the client has actually shared with the calling therapist —
// zero rows means "nothing to prefill", never an error: consent may be
// revoked between the status check and this call, and that must read as a
// silent no-op, not a toast.
export async function getMindvaultProfileByEmail(email) {
  const key = (email ?? '').trim().toLowerCase();
  if (!key) return null;
  const { data, error } = await supabase.rpc('get_mindvault_profile_by_email', {
    p_email: key,
  });
  if (error || !Array.isArray(data) || data.length === 0) {
    if (error) console.error('Error fetching mindvault profile by email:', error);
    return null;
  }
  // { mindvault_account_id, name, gender, phone_code, phone_number }
  return data[0];
}

// The New-Client form's connect state machine, derived from one
// getConnectionStatus result. Evaluation order is load-bearing:
// bookingLapsed must be checked BEFORE awaitingConsent — a lapsed booking
// keeps entitlement = 'booking', so awaitingConsent would be true too and
// the form would render the wrong "purchased, awaiting consent" copy.
//
//   null          -> { view: null }
//   connected     -> { view: 'connected' }
//   bookingLapsed -> { view: 'booking_lapsed', showCheckbox: true }
//   hasMvAccount && awaitingConsent
//                 -> { view: 'awaiting_consent', showCheckbox: false }
//   hasMvAccount  -> { view: 'not_connected', showCheckbox: true }
//   else          -> { view: 'no_account', showCheckbox: true, pendingUntilSignup: true }
export const deriveNewClientConnectState = (status) => {
  if (!status) return { view: null };
  if (status.connected) return { view: 'connected' };
  if (status.bookingLapsed) return { view: 'booking_lapsed', showCheckbox: true };
  if (status.hasMvAccount && status.awaitingConsent) {
    return { view: 'awaiting_consent', showCheckbox: false };
  }
  if (status.hasMvAccount) return { view: 'not_connected', showCheckbox: true };
  return { view: 'no_account', showCheckbox: true, pendingUntilSignup: true };
};

// mindvault_accounts.name is a single text field; the form has separate
// firstName/lastName. Deliberately simplistic split — first whitespace token
// becomes firstName, the rest lastName — because the therapist reviews the
// form before saving and can correct it. Returns null for an empty name.
export const splitMindvaultName = (name) => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

// mindvault_accounts.gender is free text; the form's Select only knows
// 'Male' | 'Female'. Anything unrecognized maps to null ("no prefill").
export const normalizeMindvaultGender = (value) => {
  const g = (value ?? '').trim().toLowerCase();
  if (g === 'male' || g === 'm' || g === '男') return 'Male';
  if (g === 'female' || g === 'f' || g === '女') return 'Female';
  return null;
};
